import React, { useCallback, useEffect, useState } from 'react';
import { Timeline, Tag, Tooltip, Spin, Empty, Segmented, Pagination, Alert } from 'antd';
import {
  CheckSquareOutlined, ProjectOutlined, SafetyCertificateOutlined,
  UserOutlined, IdcardOutlined, ScheduleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getStudentActivity } from '../../api/students';

/**
 * Öğrenciye dair işlem dökümü — kim, ne zaman, ne yaptı.
 *
 * ÖNCEDEN: yalnızca `students` tablosunun kendi değişikliklerini
 * gösteriyordu. Yönetici, öğretmenlerin takvim üzerinden yaptığı işleri
 * (yoklama, proje girişi, sertifika) göremiyordu; bir sorun çıktığında
 * "bunu kim yaptı" sorusunun cevabı yoktu. Artık hepsi tek kronolojik
 * akışta.
 *
 * "Ders Kaydı" türü sonradan eklendi (2026-08-13): yönetici "bu telafiyi kim
 * ekledi", "bu kaydı kim yaptı", "bu dersi kim sildi" sorularını da buradan
 * cevaplamak istedi. Haftalık kayıtta onlarca ders seansı oluştuğu için arka
 * uç bunları tek olaya indiriyor ve kaç ders olduğunu yazıyor — aksi halde
 * döküm tek bir kayıt işlemiyle dolardı.
 *
 * Eski sürümde iki sessiz hata vardı, ikisi de gitti:
 *  - `action` değerleri `created/updated/deleted` bekleniyordu ama arka uç
 *    `create/update/delete` yazıyor; etiket hiç eşleşmiyordu.
 *  - Sekme öğretmene de görünüyordu ama uç yalnızca yöneticiye açıktı,
 *    öğretmen tıklayınca 403 alıyordu. Sekme artık şube müdürü ve üstünde.
 *
 * Sayfalama SUNUCUDA: proje girişleri tek başına öğrenci başına yüzlerce
 * satır olabiliyor (ölçümde 117 olayın 104'ü projeydi), hepsini çekmek
 * ekranı kilitlerdi.
 */

const TUR_CONFIG = {
  yoklama:   { renk: 'blue',   ikon: <CheckSquareOutlined />,        etiket: 'Yoklama' },
  proje:     { renk: 'purple', ikon: <ProjectOutlined />,            etiket: 'Proje' },
  sertifika: { renk: 'green',  ikon: <SafetyCertificateOutlined />,  etiket: 'Sertifika' },
  kayit:     { renk: 'cyan',   ikon: <ScheduleOutlined />,           etiket: 'Ders Kaydı' },
  ogrenci:   { renk: 'orange', ikon: <IdcardOutlined />,             etiket: 'Öğrenci Kaydı' },
};

const FILTRELER = [
  { label: 'Tümü', value: '' },
  { label: 'Yoklama', value: 'yoklama' },
  { label: 'Proje', value: 'proje' },
  { label: 'Sertifika', value: 'sertifika' },
  { label: 'Ders Kaydı', value: 'kayit' },
  { label: 'Öğrenci Kaydı', value: 'ogrenci' },
];

const PER_PAGE = 50;

const StudentHistoryTab = ({ studentId }) => {
  const [olaylar, setOlaylar] = useState([]);
  const [toplam, setToplam] = useState(0);
  const [sayfa, setSayfa] = useState(1);
  const [tur, setTur] = useState('');
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState(null);

  const getir = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const res = await getStudentActivity(studentId, {
        page: sayfa,
        per_page: PER_PAGE,
        type: tur || undefined,
      });
      setOlaylar(res.data.data || []);
      setToplam(res.data.total || 0);
      setHata(null);
    } catch (e) {
      setHata(e?.response?.status === 403
        ? 'Bu dökümü görüntüleme yetkiniz yok.'
        : 'İşlem dökümü yüklenemedi.');
      setOlaylar([]);
    } finally {
      setLoading(false);
    }
  }, [studentId, sayfa, tur]);

  useEffect(() => { getir(); }, [getir]);

  if (hata) return <Alert type="warning" showIcon message={hata} />;

  const items = olaylar.map((o) => {
    const cfg = TUR_CONFIG[o.tur] || { renk: 'gray', etiket: o.tur };

    return {
      key: o.kaynak,
      color: cfg.renk,
      dot: cfg.ikon,
      children: (
        <div className="pb-2">
          <div className="flex items-center gap-2 flex-wrap">
            <Tag color={cfg.renk}>{cfg.etiket}</Tag>
            <span className="font-medium text-sm">{o.baslik}</span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <UserOutlined /> {o.kisi}
            </span>
            <Tooltip title={dayjs(o.tarih).format('DD.MM.YYYY HH:mm:ss')}>
              <span className="text-xs text-gray-400">
                {dayjs(o.tarih).format('DD.MM.YYYY HH:mm')}
              </span>
            </Tooltip>
          </div>
          {o.aciklama && (
            <div className="text-xs text-gray-600 mt-1">{o.aciklama}</div>
          )}
        </div>
      ),
    };
  });

  return (
    <div className="max-w-3xl">
      <div className="mb-4">
        <Segmented
          options={FILTRELER}
          value={tur}
          onChange={(v) => { setTur(v); setSayfa(1); }}
          size="small"
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Spin /></div>
      ) : olaylar.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={tur ? 'Bu türde kayıt yok.' : 'Henüz işlem kaydı yok.'}
        />
      ) : (
        <>
          <Timeline items={items} />
          {toplam > PER_PAGE && (
            <Pagination
              current={sayfa}
              pageSize={PER_PAGE}
              total={toplam}
              onChange={setSayfa}
              showSizeChanger={false}
              showTotal={(t) => `Toplam ${t} işlem`}
              size="small"
            />
          )}
        </>
      )}
    </div>
  );
};

export default StudentHistoryTab;
